import { useState, useRef, useEffect } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Chip,
    ClickAwayListener,
    Divider,
    Paper,
    Popper,
    Stack,
    Typography
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'components/ui-component/cards/MainCard';
import Transitions from 'components/ui-component/extended/Transitions';

// assets
import { IconSettings, IconUserFilled } from '@tabler/icons-react';

import { ProfileListUser, ProfileListAdmin } from './list';
import { PERMISSIONS } from 'store/baseConfig';
// import User1 from 'assets/images/users/user-round.svg';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = ({perm, userName, userRole, userSubName, isMobile}) => {
    const theme = useTheme();

    // const [sdm, setSdm] = useState(true);
    // const [value, setValue] = useState('');
    // const [notification, setNotification] = useState(false);
    // const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);

    // React 19 호환성을 위해 ref callback 사용
    const setAnchorRef = (element) => {
        anchorRef.current = element;
    };


    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleToggle = (event) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        setOpen((prevOpen) => {
            const newState = !prevOpen;
            return newState;
        });
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            if (anchorRef.current) {
                anchorRef.current.focus();
            }
        }

        prevOpen.current = open;
    }, [open]);


    return (
        <>
            <Chip
                clickable
                sx={{
                    height: isMobile ? '40px' : '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.primary.light,
                    minWidth: isMobile ? 'auto' : 'unset',
                    maxWidth: isMobile ? '200px' : 'unset',
                    cursor: 'pointer',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    userSelect: 'none',
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0,
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        padding: isMobile ? '0 8px' : '0 12px',
                        pointerEvents: 'none'
                    },
                    '& .MuiChip-icon': {
                        pointerEvents: 'none'
                    }
                }}
                icon={
                    !isMobile ? (
                        <Avatar
                            src={IconUserFilled.toString()}
                            sx={{
                                ...theme.typography.mediumAvatar,
                                margin: '8px 0 8px 8px !important',
                                pointerEvents: 'none'
                            }}
                        />
                    ) : null
                }
                label={<Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: isMobile ? '0.25rem' : '0.5rem'
                }}>
                    <Typography sx={{ 
                        marginRight: isMobile ? '0.25rem' : '0.5rem',
                        fontSize: isMobile ? '0.75rem' : 'inherit'
                    }}>
                        {`${userName}(${userSubName})`}
                    </Typography>
                    <IconSettings 
                        stroke={1.5} 
                        size={isMobile ? "1.2rem" : "1.5rem"} 
                        color={theme.palette.primary.main} 
                    />
                </Box>}
                variant="outlined"
                ref={setAnchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(e);
                }}
                color="primary"
            />
            <Popper
                placement="bottom-end"
                open={open && !!anchorRef.current}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                sx={{
                    zIndex: 1300
                }}
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        },
                        {
                            name: 'preventOverflow',
                            enabled: true
                        },
                        {
                            name: 'flip',
                            enabled: true
                        }
                    ]
                }}
            >
                {({ TransitionProps, placement }) => {
                    // Popper의 placement를 Transitions의 position으로 변환
                    let position = 'top-left';
                    if (placement?.includes('bottom')) {
                        position = placement.includes('end') ? 'bottom-right' : placement.includes('start') ? 'bottom-left' : 'bottom';
                    } else if (placement?.includes('top')) {
                        position = placement.includes('end') ? 'top-right' : placement.includes('start') ? 'top-left' : 'top';
                    }
                    
                    return (
                        <Transitions type="grow" in={open} {...TransitionProps} position={position}>
                            <Paper>
                                <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Box sx={{ p: 2 }}>
                                            <Stack>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Typography variant="h4">안녕하세요,</Typography>
                                                    <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                                        {userName}님
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="subtitle2">{userRole}</Typography>
                                            </Stack>
                                        </Box>
                                        <Divider />
                                        <PerfectScrollbar
                                            style={{
                                                height: '100%',
                                                maxHeight: 'calc(100vh - 250px)',
                                                overflowX: 'hidden'
                                            }}
                                        >
                                            <Box sx={{ px: 2 }}>
                                                {perm === PERMISSIONS.SUPER_ADMIN || perm === PERMISSIONS.ADMIN ? <ProfileListAdmin /> : <ProfileListUser />}
                                            </Box>
                                        </PerfectScrollbar>
                                    </MainCard>
                                </ClickAwayListener>
                            </Paper>
                        </Transitions>
                    );
                }}
            </Popper>
        </>
    );
};

export default ProfileSection;